/**
 * A card's picture until it has a real one: a drawing of its kind. A map's
 * rendered shot replaces it once a save has made one (the thumbnail commit).
 * Declarative SVG; colours from the theme.
 */
import type { ReactElement } from 'react';
import type { AssetKind } from '../../engine/assets/types';

const STROKE = 'var(--accent)';

function FolderArt() {
  return <path d="M36 22 h22 l6 6 h32 v38 h-60 Z" fill="var(--accent-dim)" stroke={STROKE} />;
}

function MapArt() {
  return (
    <g fill="var(--accent-dim)" stroke={STROKE} strokeWidth={0.8}>
      <path d="M30 48 L66 30 L102 48 L66 66 Z" fill="none" strokeOpacity={0.5} />
      <path d="M50 44 L62 38 L74 44 L62 50 Z M50 44 V32 L62 26 L74 32 V44 M62 38 V26" />
      <path d="M76 52 L84 48 L92 52 L84 56 Z M76 52 V46 L84 42 L92 46 V52" />
    </g>
  );
}

function PaletteArt() {
  const swatches = [22, 40, 58, 76, 94];
  return (
    <g stroke={STROKE}>
      {swatches.map((x, i) => (
        <rect key={x} x={x} y={26} width={14} height={26} fill="var(--accent-dim)" strokeOpacity={1 - i * 0.15} />
      ))}
    </g>
  );
}

function SceneArt() {
  return (
    <g fill="var(--accent-dim)" stroke={STROKE} strokeWidth={0.8}>
      <path d="M30 50 L66 32 L102 50 L66 68 Z" fill="none" strokeOpacity={0.5} />
      <path d="M54 44 L62 40 L70 44 L62 48 Z M54 44 V36 L62 32 L70 36 V44 M62 40 V32" />
      <circle cx={80} cy={54} r={3} fill={STROKE} />
      <circle cx={46} cy={54} r={3} fill={STROKE} />
      <circle cx={88} cy={47} r={3} fill={STROKE} />
    </g>
  );
}

function CharacterArt() {
  return (
    <g fill="var(--accent-dim)" stroke={STROKE} strokeWidth={0.8}>
      <circle cx={66} cy={24} r={7} />
      <path d="M52 66 V44 Q52 34 66 34 Q80 34 80 44 V66 M58 66 V50 M74 66 V50" />
    </g>
  );
}

/** The canvas's drawings: a page with lines of text, a framed landscape. */
function HandoutArt() {
  return (
    <g stroke={STROKE} strokeWidth={0.8}>
      <rect x={44} y={8} width={44} height={62} fill="var(--accent-dim)" />
      {[18, 25, 32, 39, 46].map((y, i) => (
        <line key={y} x1={50} x2={82 - (i % 2) * 6} y1={y} y2={y} strokeOpacity={0.7} />
      ))}
    </g>
  );
}

function ImageArt() {
  return (
    <g stroke={STROKE} strokeWidth={0.8}>
      <rect x={30} y={12} width={72} height={54} fill="none" />
      <path d="M30 60 L54 38 L70 52 L80 44 L102 62" fill="var(--accent-dim)" />
      <circle cx={86} cy={26} r={5} fill={STROKE} />
    </g>
  );
}

/**
 * The Armoury's (plan armoury): typographic, never a drawing of a weapon (the ruling: data plates only). A weapon is
 * a small data plate; a loadout, plates stacked; a template, its outline; an effect, a ring; a modifier, its sign.
 */
function PlateLines({ x, y }: { x: number; y: number }) {
  return (
    <g stroke={STROKE} strokeWidth={0.8}>
      <rect x={x} y={y} width={60} height={30} fill="var(--accent-dim)" />
      <line x1={x + 6} x2={x + 34} y1={y + 9} y2={y + 9} />
      {[0, 1, 2].map((i) => (
        <rect key={i} x={x + 6 + i * 17} y={y + 15} width={14} height={9} fill="none" strokeOpacity={0.7} />
      ))}
    </g>
  );
}

const WeaponArt = () => <PlateLines x={36} y={24} />;

function LoadoutArt() {
  return (
    <g>
      <PlateLines x={44} y={14} />
      <PlateLines x={36} y={24} />
      <PlateLines x={28} y={34} />
    </g>
  );
}

function AreaArt() {
  return (
    <g stroke={STROKE} strokeWidth={0.8} fill="none">
      <circle cx={66} cy={39} r={22} strokeDasharray="3 3" />
      <circle cx={66} cy={39} r={3} fill={STROKE} />
    </g>
  );
}

function EffectArt() {
  return (
    <g stroke={STROKE} strokeWidth={0.8}>
      <circle cx={66} cy={39} r={18} fill="var(--accent-dim)" />
      <circle cx={66} cy={39} r={9} fill="none" strokeOpacity={0.7} />
    </g>
  );
}

function ModifierArt() {
  return (
    <text x={66} y={50} textAnchor="middle" fontSize={30} fill={STROKE} fontFamily="ui-monospace, monospace">
      ±10
    </text>
  );
}

function ModuleArt() {
  return (
    <g stroke={STROKE} strokeWidth={0.8}>
      <rect x={50} y={23} width={32} height={32} fill="var(--accent-dim)" />
      {[56, 66, 76].map((x) => (
        <line key={x} x1={x} x2={x} y1={17} y2={23} />
      ))}
      {[56, 66, 76].map((x) => (
        <line key={x} x1={x} x2={x} y1={55} y2={61} />
      ))}
    </g>
  );
}

/** Tactics: a grid of ticks - abilities as rows, Oaths as columns, as its editor draws them. */
function TacticsArt() {
  return (
    <g stroke={STROKE} strokeWidth={0.8} fill="none">
      {[24, 36, 48].map((y) => (
        <line key={y} x1={44} x2={88} y1={y} y2={y} />
      ))}
      {[60, 72, 84].map((x) => (
        <line key={x} x1={x} x2={x} y1={16} y2={54} />
      ))}
      <path d="M62 30 l3 3 l5 -6 M74 42 l3 3 l5 -6 M62 42 l3 3 l5 -6" stroke="var(--accent)" />
    </g>
  );
}

/** Always on: a steady line with no switch on it. */
function AlwaysOnArt() {
  return (
    <g stroke={STROKE} strokeWidth={0.8} fill="none">
      <circle cx={66} cy={35} r={14} />
      <line x1={46} x2={86} y1={35} y2={35} stroke="var(--accent)" />
    </g>
  );
}

/** A Solo mode: a switch, lever up. */
function SoloModeArt() {
  return (
    <g stroke={STROKE} strokeWidth={0.8} fill="none">
      <rect x={54} y={22} width={24} height={30} rx={4} />
      <line x1={66} x2={72} y1={44} y2={26} stroke="var(--accent)" />
      <circle cx={72} cy={26} r={3} fill="var(--accent-dim)" />
    </g>
  );
}

const ART: Record<AssetKind | 'folder', () => ReactElement> = {
  folder: FolderArt,
  map: MapArt,
  palette: PaletteArt,
  scene: SceneArt,
  character: CharacterArt,
  handout: HandoutArt,
  image: ImageArt,
  weapon: WeaponArt,
  loadout: LoadoutArt,
  area: AreaArt,
  effect: EffectArt,
  modifier: ModifierArt,
  module: ModuleArt,
  tactics: TacticsArt,
  alwaysOn: AlwaysOnArt,
  soloMode: SoloModeArt,
};

export default function AssetGlyph({ kind, className }: { kind: AssetKind | 'folder'; className?: string }) {
  const Art = ART[kind];
  return (
    <svg className={className} viewBox="0 0 132 78" aria-hidden="true">
      <Art />
    </svg>
  );
}
